import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getChargesSociales, updateLigne } from '../../../api/charges_sociales';
import { useP } from '../../../i18n/useP';
import '../../ChargesSocialesPage.css';

/* Phasing du paiement des charges sociales (assurances) — vue CASH.
   Déplacé depuis Input People → Charges sociales : là-bas on saisit les TAUX
   (coût) ; ici on planifie l'ÉCHÉANCIER de paiement (sortie de trésorerie).
   Même découplage que les coûts indirects (base costing vs phasing cash).
   Source de données unique : table charges_sociales_lignes (champ payment_months). */

const MOIS_LETTERS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

export function PaymentChips({ value, onChange, disabled }) {
  const p = useP();
  const months = Array.isArray(value) ? value : [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  function toggle(m) {
    if (disabled) return;
    const active = months.includes(m);
    const next = active ? months.filter((x) => x !== m) : [...months, m].sort((a, b) => a - b);
    onChange(next.length === 12 ? null : next);
  }
  /* « TOUS » (demande d'Olivier, 2026-09-28) : tous les mois d'un geste, ou
     aucun, pour n'en choisir ensuite que quelques-uns. */
  const all = months.length === 12;
  return (
    <div className="cs-chips">
      <button type="button" className={`cs-chip cs-chip--all${all ? ' is-active' : ''}`}
              onClick={() => !disabled && onChange(all ? [] : null)} disabled={disabled}>
        {p('TOUS')}
      </button>
      {MOIS_LETTERS.map((label, i) => (
        <button
          key={i}
          type="button"
          className={`cs-chip ${months.includes(i) ? 'is-active' : ''}`}
          onClick={() => toggle(i)}
          disabled={disabled}
          title={`${p('Mois')} ${i + 1}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export default function SocialPhasingTab({ tenantId, year, addToast, locked, onSaved, allYears }) {
  const { t } = useTranslation();
  const p = useP();
  const [lignes, setLignes] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!tenantId || !year) { setLignes([]); return; }
    setLoading(true);
    getChargesSociales(tenantId, year)
      .then((res) => setLignes((res.data || []).filter((l) => l.section === 'assurance')))
      .catch(() => addToast && addToast(p('Erreur chargement charges sociales'), 'error'))
      .finally(() => setLoading(false));
  }, [tenantId, year]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  async function save(id, payment_months) {
    const res = await updateLigne(tenantId, id, { payment_months });
    if (res.data) {
      setLignes((prev) => prev.map((l) => (l.id === id ? res.data : l)));
      onSaved && onSaved();
    } else addToast && addToast(res.error || p('Erreur sauvegarde'), 'error');
  }

  return (
    <section className="cs-phasing-block">
      <div className="cs-phasing-block__title">
        {p('Phasing du paiement — charges sociales (assurances)')}
        {allYears ? p(' · valable toutes années') : ` · ${year}`}
      </div>
      <div className="cs-phasing-block__hint">
        {p('Mois de paiement des assurances standard.')}
      </div>
      {loading && <p className="cs-loading">{t('common:status.loading')}</p>}
      {!loading && lignes.length === 0 && (
        <p className="cs-phasing-block__hint">
          {p('Aucune assurance pour')} {year}
          {p(". Saisis-les d'abord dans Input People → Charges sociales.")}
        </p>
      )}
      {!loading && lignes.length > 0 && (
        <table className="cs-phasing-block__table">
          <tbody>
            {/* Les cotisations de base se paient chaque mois : la ligne le dit
                avant la première assurance, sinon on cherche où elles sont
                (demande d'Olivier, 2026-09-27). */}
            <tr>
              <td className="cs-phasing-block__name">{p('Cotisations de base')}</td>
              <td className="cs-phasing-block__cells cs-phasing-block__fixed">{p('paiement mensuel')}</td>
            </tr>
            {lignes.map((l) => (
              <tr key={l.id}>
                <td className="cs-phasing-block__name">{l.libelle}</td>
                <td className="cs-phasing-block__cells">
                  <PaymentChips value={l.payment_months} disabled={locked} onChange={(v) => save(l.id, v)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
